import { useId } from "react";
import type { JSX, ReactNode } from "react";
import { useI18n } from "../i18n/I18nContext";
import { cx } from "./classNames";
import { optionalMarkAttribute, useFormError } from "./formErrors";
import styles from "./ui.module.css";

/** Attributes that connect a form control with its {@link Field}. */
export interface FieldControlProps {
  /** Identifier of the control, referenced by the label. */
  readonly id: string;
  /** Identifiers of hint and error, or `undefined` without both. */
  readonly "aria-describedby": string | undefined;
  /** `true` while the field has an error, otherwise `undefined` (no attribute). */
  readonly "aria-invalid": true | undefined;
}

/** Properties of {@link Field}. */
export interface FieldProps {
  /** Visible label. */
  readonly label: ReactNode;
  /** Hint below the label, for example the expected format. */
  readonly hint?: ReactNode;
  /**
   * Error message; while set, the control is marked invalid. Without one, the field shows the
   * error a surrounding `ValidatedForm` found at its control (F-60).
   */
  readonly error?: string | null | undefined;
  /**
   * Whether the field may stay empty. Optional fields are marked „(optional)“; required fields
   * carry no mark (one marking rule, F-1, UX-111).
   */
  readonly optional?: boolean | undefined;
  /** Additional class name of the field. */
  readonly className?: string | undefined;
  /**
   * Renders the control with the given attributes, for example
   * `{(control) => <input {...control} value={value} onChange={…} />}`.
   *
   * @param control - Attributes to spread onto the control.
   * @returns The control.
   */
  readonly children: (control: FieldControlProps) => ReactNode;
}

/**
 * Form field with visible label, optional hint and error (F-1, F-2, WCAG 1.3.1, 3.3.1, UX-111);
 * inside a `ValidatedForm` it also shows the error the form found at its control (F-60).
 * Hint and error are connected to the control with `aria-describedby`, an error sets
 * `aria-invalid`, and optional fields are marked „(optional)“. The order label – hint – error –
 * control keeps hint and error in view while typing.
 *
 * @param props - Component properties.
 * @returns The field.
 */
export function Field(props: FieldProps): JSX.Element {
  const { t } = useI18n();
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const hasHint = props.hint !== undefined && props.hint !== null;
  const formError = useFormError(id);
  const ownError = props.error ?? null;
  const error = ownError === null || ownError === "" ? formError : ownError;
  const hasError = error !== null && error !== "";
  const describedBy = [hasHint ? hintId : null, hasError ? errorId : null]
    .filter((value) => value !== null)
    .join(" ");
  const control: FieldControlProps = {
    id,
    "aria-describedby": describedBy === "" ? undefined : describedBy,
    "aria-invalid": hasError ? true : undefined,
  };
  const field = (
    <div className={cx(styles["field"], props.className)}>
      <label htmlFor={id} className={styles["fieldLabel"]}>
        {props.label}
        {props.optional === true ? (
          <>
            {" "}
            <span className={styles["fieldOptional"]} {...{ [optionalMarkAttribute]: "" }}>
              {t("field.optional")}
            </span>
          </>
        ) : null}
      </label>
      {hasHint ? (
        <p id={hintId} className={cx(styles["muted"], styles["fieldHint"])}>
          {props.hint}
        </p>
      ) : null}
      {hasError ? (
        <p id={errorId} className={styles["fieldError"]}>
          {error}
        </p>
      ) : null}
      {props.children(control)}
    </div>
  );

  return field;
}

/**
 * Adds an error that the form shows outside the field – for example one message for a range of
 * two dates, or a message next to the buttons – to the attributes of a field's control, so the
 * control is still invalid and described by it. The field's own hint and error stay (F-60); spread
 * the result instead of the attributes, never next to them.
 *
 * @param control - The attributes the field passes to its control.
 * @param errorId - Identifier of the message, or `null` while there is none.
 * @returns The attributes to spread onto the control.
 */
export function withOwnError(
  control: FieldControlProps,
  errorId: string | null,
): FieldControlProps {
  const merged: FieldControlProps = {
    ...withDescription(control, errorId),
    "aria-invalid": errorId === null ? control["aria-invalid"] : true,
  };

  return merged;
}

/**
 * Adds a description that stands outside the field – for example a notice below the form that
 * belongs to the chosen value – to the attributes of a field's control, keeping the field's own
 * hint and error (F-60); spread the result instead of the attributes, never next to them.
 *
 * @param control - The attributes the field passes to its control.
 * @param descriptionId - Identifier of the description, or `null` while there is none.
 * @returns The attributes to spread onto the control.
 */
export function withDescription(
  control: FieldControlProps,
  descriptionId: string | null,
): FieldControlProps {
  const describedBy = [control["aria-describedby"], descriptionId ?? undefined]
    .filter((value) => value !== undefined && value !== "")
    .join(" ");
  const merged: FieldControlProps = {
    ...control,
    "aria-describedby": describedBy === "" ? undefined : describedBy,
  };

  return merged;
}

/** Properties of {@link ControlField}. */
export interface ControlFieldProps {
  /**
   * Renders the control with the given attributes; the control names itself, for example with
   * `aria-label` in a table cell.
   *
   * @param control - Attributes to spread onto the control.
   * @returns The control.
   */
  readonly children: (control: FieldControlProps) => ReactNode;
}

/**
 * A control without a visible label of its own, for example an input in a table cell that is named
 * by `aria-label`, with the error a surrounding `ValidatedForm` found at it below the control,
 * tied with `aria-describedby` and `aria-invalid` (F-60, UX-008).
 *
 * @param props - Component properties.
 * @returns The control and its error.
 */
export function ControlField(props: ControlFieldProps): JSX.Element {
  const id = useId();
  const errorId = `${id}-error`;
  const error = useFormError(id);
  const control: FieldControlProps = {
    id,
    "aria-describedby": error === null ? undefined : errorId,
    "aria-invalid": error === null ? undefined : true,
  };
  const field = (
    <>
      {props.children(control)}
      {error === null ? null : (
        <p id={errorId} className={styles["fieldError"]}>
          {error}
        </p>
      )}
    </>
  );

  return field;
}
